import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-wazin-navy-dark via-wazin-navy to-wazin-navy-dark text-white border-t border-wazin-gold" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="col-span-1">
            <div className="mb-6">
              <img 
                src="/images/wazin-logo.png" 
                alt="Wazin Logo" 
                className="h-16 md:h-20 w-auto object-contain"
              />
            </div>
            <p className="text-[#9D7E4F]-100 text-sm leading-relaxed">
              {i18n.language === 'ar' 
                ? 'شركة قانونية متخصصة في تقديم الحلول القانونية الشاملة'
                : 'Professional law firm providing comprehensive legal solutions'
              }
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#9D7E4F]">
              {i18n.language === 'ar' ? 'روابط سريعة' : 'Quick Links'}
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-300 hover:text-[#9D7E4F] transition-colors text-sm flex items-center gap-2">
                  <span className="text-[#9D7E4F]">›</span>
                  {t('home')}
                </Link>
              </li>
              <li>
                <Link to="/#services" className="text-gray-300 hover:text-[#9D7E4F] transition-colors text-sm flex items-center gap-2">
                  <span className="text-[#9D7E4F]">›</span>
                  {t('services')}
                </Link>
              </li>
              <li>
                <Link to="/#departments" className="text-gray-300 hover:text-[#9D7E4F] transition-colors text-sm flex items-center gap-2">
                  <span className="text-[#9D7E4F]">›</span>
                  {t('departments')}
                </Link>
              </li>
              <li>
                <Link to="/#contact" className="text-gray-300 hover:text-[#9D7E4F] transition-colors text-sm flex items-center gap-2">
                  <span className="text-[#9D7E4F]">›</span>
                  {t('contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#9D7E4F]">
              {t('services')}
            </h4>
            <ul className="space-y-3">
              <li className="text-gray-300 text-sm flex items-center gap-2">
                <span className="text-[#9D7E4F]">›</span>
                {t('litigation')}
              </li>
              <li className="text-gray-300 text-sm flex items-center gap-2">
                <span className="text-[#9D7E4F]">›</span>
                {t('contracts')}
              </li>
              <li className="text-gray-300 text-sm flex items-center gap-2">
                <span className="text-[#9D7E4F]">›</span>
                {t('intellectualProperty')}
              </li>
              <li className="text-gray-300 text-sm flex items-center gap-2">
                <span className="text-[#9D7E4F]">›</span>
                {t('cybercrimes')}
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#9D7E4F]">
              {t('contactUs')}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-300 text-sm hover:text-[#9D7E4F] transition-colors">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#9D7E4F]" />
                <a href="mailto:info@wazin.law" className="hover:text-wazin-gold transition-colors">
                  info@wazin.law
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-300 text-sm hover:text-[#9D7E4F] transition-colors">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#9D7E4F]" />
                <a href="tel:+966122226665" dir="ltr" className="hover:text-wazin-gold transition-colors">
                  +966 12 222 6665
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-300 text-sm hover:text-[#9D7E4F] transition-colors">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#9D7E4F]" />
                <div>
                  <div className="font-semibold text-wazin-gold mb-1">
                    {i18n.language === 'ar' ? 'المقر الرئيسي:' : 'Main Office:'}
                  </div>
                  <div>
                    {i18n.language === 'ar'
                      ? 'جدة – حي الرويس – المملكة العربية السعودية'
                      : 'Jeddah – Al Rawais District – Saudi Arabia'
                    }
                  </div>
                  <div className="text-xs mt-1 text-gray-400">
                    {i18n.language === 'ar'
                      ? 'نخدم عملاءنا في جميع مناطق المملكة'
                      : 'We serve clients across all regions of the Kingdom'
                    }
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {currentYear} <span className="text-[#9D7E4F] font-semibold">{i18n.language === 'ar' ? 'شركة وازن للمحاماة' : 'Wazin Law Firm'}</span>. {t('allRightsReserved')}
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-gray-400 hover:text-[#9D7E4F] transition-colors text-sm font-medium">
              {t('privacyPolicy')}
            </Link>
            <Link to="/terms" className="text-gray-400 hover:text-[#9D7E4F] transition-colors text-sm font-medium">
              {t('termsConditions')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
